import { z } from 'zod';
/**
 * Admin roles.
 *
 * Two DISJOINT families, not one ladder:
 *   - support staff `owner > supervisor > member > viewer` — the original
 *     hierarchy; every support/risk/content/system policy is written for it;
 *   - `marketing` — a scoped role: the «Реклама» workspace ONLY, with the full
 *     set of `ads.*` rights (owner level inside the section) and NOTHING else —
 *     no conversations, customers, billing, settings, MCP agent data. Not a rung
 *     of the support ladder: it is neither above nor below `viewer`.
 *
 * A new role is DENY-BY-DEFAULT: policies are explicit allow-lists over roles
 * (`packages/rbac/src/policies.ts`), never `role !== 'x'`, and admin-api adds a
 * route-prefix scope on top (`ROLE_API_SCOPES` in `@wit-support/rbac`).
 */
export const ROLE_VALUES = ['owner', 'supervisor', 'member', 'viewer', 'marketing'];
/** Human labels (UI badges, selects). Support roles keep their historical English names. */
export const ROLE_LABELS = {
    owner: 'Owner',
    supervisor: 'Supervisor',
    member: 'Member',
    viewer: 'Viewer',
    marketing: 'Маркетинг',
};
/** Short role descriptions for pickers. */
export const ROLE_DESCRIPTIONS = {
    owner: 'Полный доступ ко всей админке',
    supervisor: 'Руководитель поддержки',
    member: 'Оператор поддержки',
    viewer: 'Только просмотр поддержки',
    marketing: 'Только раздел «Реклама», полный доступ',
};
const ROLE_OPTIONS = ROLE_VALUES.map((value) => ({ value, label: ROLE_LABELS[value] }));
const ROLE_FIELD_DESCRIPTION = 'Маркетинг — только раздел «Реклама» (полный доступ), без поддержки, клиентов и настроек.';
export const Role = z.enum(ROLE_VALUES).meta({
    label: 'Role',
    options: ROLE_OPTIONS,
});
/**
 * Rank on the SUPPORT ladder. `marketing` is outside the ladder and ranks 0 —
 * below `viewer` — so any `ROLE_ORDER[role] >= ROLE_ORDER.viewer` style check
 * can never let it in by accident.
 */
export const ROLE_ORDER = {
    owner: 4,
    supervisor: 3,
    member: 2,
    viewer: 1,
    marketing: 0,
};
/** Support-staff roles — the original ladder. Everything except `marketing`. */
export const SUPPORT_ROLES = [
    'owner',
    'supervisor',
    'member',
    'viewer',
];
/** True for the support ladder (owner/supervisor/member/viewer); false for scoped roles. */
export function isSupportRole(role) {
    return SUPPORT_ROLES.includes(role);
}
export const AdminId = z.guid().brand();
/**
 * Fixed-uuid SYSTEM actor. Automated sends (e.g. the pipeline runner driving
 * `ReplySendPort` from the worker) have no logged-in admin, but the orchestrator
 * still resolves `actor` against the `admin` table and audit-log FKs reference
 * `admin(id)`. The `system_actor` migration INSERTs a deterministic, non-login
 * admin row with this id so those lookups + FKs succeed. Never used for login.
 */
export const SYSTEM_ACTOR_ID = '00000000-0000-0000-0000-0000000000a1';
export const CreateAdminRequest = z.object({
    email: z
        .string()
        .email('Enter a valid email address')
        .meta({ label: 'Email', placeholder: 'name@example.com' }),
    displayName: z
        .string()
        .min(1, 'Required')
        .max(128, 'Must be 128 characters or fewer')
        .meta({ label: 'Display name' }),
    role: Role.meta({
        label: 'Role',
        description: ROLE_FIELD_DESCRIPTION,
        options: ROLE_OPTIONS,
    }),
    // Required. Transmitted over HTTPS only. Stored as a hash in better-auth's
    // `account` table — never persisted to `admin`.
    password: z
        .string()
        .min(8, 'At least 8 characters')
        .max(128, 'Must be 128 characters or fewer')
        .meta({
        label: 'Initial password',
        description: 'At least 8 characters. The invitee should change it on first login.',
        secret: true,
    }),
    timezone: z.string().min(1).optional().meta({
        label: 'Timezone',
        description: 'IANA zone like Europe/Moscow. Defaults to UTC.',
    }),
    telegramId: z.string().regex(/^\d+$/, 'Digits only').optional().meta({
        label: 'Telegram ID',
        description: 'Numeric Telegram user id; enables bot actions for this operator.',
    }),
});
export const ChangePasswordRequest = z.object({
    currentPassword: z
        .string()
        .min(1, 'Enter your current password')
        .meta({ label: 'Current password', secret: true }),
    newPassword: z
        .string()
        .min(8, 'At least 8 characters')
        .max(128, 'Must be 128 characters or fewer')
        .meta({
        label: 'New password',
        description: 'At least 8 characters.',
        secret: true,
    }),
});
export const UpdateAdminRequest = z.object({
    displayName: z
        .string()
        .min(1, 'Required')
        .max(128, 'Must be 128 characters or fewer')
        .optional()
        .meta({ label: 'Display name' }),
    role: Role.optional().meta({
        label: 'Role',
        description: ROLE_FIELD_DESCRIPTION,
        options: ROLE_OPTIONS,
    }),
    timezone: z.string().min(1).optional().meta({ label: 'Timezone' }),
    telegramId: z.string().regex(/^\d+$/, 'Digits only').optional().meta({ label: 'Telegram ID' }),
});
export const Admin = z.object({
    id: AdminId,
    email: z.string().email(),
    displayName: z.string().min(1).max(128),
    role: Role,
    channelIds: z.array(z.guid()),
    timezone: z.string().min(1),
    telegramId: z.string().nullable().default(null),
    disabled: z.boolean(),
    /** Agent-authored HTML signature. NULL → server substitutes a default
     *  "Warm regards,<br/>{{displayName}}<br/>WIT LAB Team" block at send. */
    signatureHtml: z.string().nullable().default(null),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
});
//# sourceMappingURL=admin.js.map